// ── usePolyUnrealizedPnl — the Polymarket twin of usePositionPnl's total ──
//
// Sums (live side-native mark − exact basis − fees) over the trader's open
// poly positions — the same formula as the Position Tracker's headline tile
// poly branch, so every surface shows the same number. Marks come from
// polyPositionLiveData (slug mid via the store's conditionId index);
// positions whose market isn't in the live feed contribute 0 rather than a
// fake number.
//
// Returns null until the positions endpoint has answered at least once
// (callers render '—'), then a cents total.

import { useMemo } from 'react';
import { usePolymarketRelayPositions } from '@/hooks/usePolymarketRelayPositions';
import { polyPositionLiveData } from '@/lib/polymarket/describeOrder';
import { useThrottledMarketVersion } from '@/hooks/ui/useThrottledMarketVersion';

export function usePolyUnrealizedPnl(enabled: boolean = true): number | null {
  const { positions, status } = usePolymarketRelayPositions({
    enabled,
    pollMs: 30_000,
    includePnl: false,
  });
  const version = useThrottledMarketVersion();

  return useMemo(() => {
    void version; // marks are imperative store reads — re-derive on ticks
    if (status !== 'ready') return null;
    let sum = 0;
    for (const p of positions) {
      const nowCents = polyPositionLiveData(p.ticker, p.side).nowCents;
      if (nowCents == null) continue;
      const entryCost = p.costBasisCents ?? p.avgEntryPriceCents * p.netCount;
      sum += nowCents * p.netCount - (entryCost + (p.feesPaidCents ?? 0));
    }
    return sum;
  }, [positions, status, version]);
}
